/**
 * SF-5057: force delete on the Deleted companies tab. Staff with the force permission tick
 * waiting deletions of recently made Demo or Test companies and delete them now instead of
 * after the waiting period. No bulk route exists: one POST per company through the shared
 * batch runner. Ticks are checked again against each fresh copy of the list, and sf-api
 * re-checks every rule when it writes, so a stale tick at worst gets a refusal.
 */
import type { DataProvider } from '@refinedev/core';
import { runBatch, type BatchResult } from '../../utils/batch';
import { forceDeletionUrl, type CompanyDeletionRow } from './api/companyDeleteClient';
import { canEditCompanyId } from './api/editableCompaniesClient';
import { accountTypeLabel } from './constants';

/**
 * Mirrors sf-api's FORCE_DELETE_MAX_COMPANY_AGE_DAYS (Alan's rule, SF-5057). Only used to
 * explain a disabled tick box; whether a row can be forced comes from the row's `can_force`.
 */
const FORCE_MAX_COMPANY_AGE_DAYS = 30;
const DAY_MS = 86_400_000;

/** Shown when sf-api has no force route (an older server answers a bare 404). */
export const FORCE_NOT_SUPPORTED_MESSAGE = "This server can't force delete yet";

/** True when this sf-api sends `can_force` on every row (an older one sends none). */
export const rowsSupportForce = (rows: CompanyDeletionRow[]): boolean =>
  rows.length > 0 && rows.every((r) => typeof r.can_force === 'boolean');

/**
 * Why a row can't be ticked for force delete, or null when it can. The reasons follow the
 * server's rules in order; `can_force` stays the deciding answer. `editScope` is the settled
 * sf-api company list (null = unknown, nothing tickable).
 */
export function forceTickBlock(
  row: CompanyDeletionRow,
  editScope: unknown,
  now: number = Date.now(),
): string | null {
  if (row.status !== 'scheduled') return 'Only waiting deletions can be deleted now';
  if (row.forced_at) return 'Already being deleted now';
  const type = row.company_account_type;
  // No stored type reads as Paid on the server (the schema default).
  if (!type || type === 'Paid') return "Paid companies can't be force deleted";
  if (type !== 'Demo' && type !== 'Test') {
    return `${accountTypeLabel(type)} companies can't be force deleted`;
  }
  const created = row.company_created_at ? new Date(row.company_created_at).getTime() : NaN;
  if (Number.isNaN(created)) return "We don't know when this company was created";
  if (now - created >= FORCE_MAX_COMPANY_AGE_DAYS * DAY_MS) return 'Created more than 30 days ago';
  if (row.can_force !== true) return "Can't be deleted now";
  if (!canEditCompanyId(editScope, row.company_id)) return 'Not on your list of companies';
  return null;
}

/**
 * The ticks after the In progress list (or the company list) loads again: each ticked row on
 * the loaded page becomes its fresh copy, and is unticked when it can no longer be ticked
 * (forced by someone else, no longer waiting, or off the person's list). Ticks on other pages
 * stay; sf-api refuses any that are no longer allowed. Returns `ticked` itself when nothing
 * changed, so React skips the re-render.
 */
export function reconcileForceTicks(
  ticked: Map<string, CompanyDeletionRow>,
  loadedRows: CompanyDeletionRow[],
  editScope: unknown,
  now: number = Date.now(),
): Map<string, CompanyDeletionRow> {
  const fresh = new Map(loadedRows.map((r) => [r._id, r] as const));
  let changed = false;
  const next = new Map<string, CompanyDeletionRow>();
  ticked.forEach((row, id) => {
    const latest = fresh.get(id);
    if (!latest) {
      next.set(id, row);
      return;
    }
    changed = changed || latest !== row;
    if (forceTickBlock(latest, editScope, now) === null) next.set(id, latest);
    else changed = true;
  });
  return changed ? next : ticked;
}

interface HttpError {
  response?: { status?: number; data?: { message?: unknown; code?: unknown } };
}

const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v : undefined);

/**
 * Readable copy for a failed force. A 5xx (or no reply) never shows the server's text, which
 * may be a raw exception. A 404 with no `code` is an sf-api without the force route.
 */
export function forceErrorMessage(err: unknown): string {
  const response = (err as HttpError | undefined)?.response;
  const status = response?.status;
  if (!status || status >= 500) return 'Something went wrong. Please try again.';
  if (status === 404 && !text(response?.data?.code)) return FORCE_NOT_SUPPORTED_MESSAGE;
  if (status === 403) return 'You are not allowed to force delete this company.';
  return text(response?.data?.message) ?? `Request failed (${status})`;
}

/**
 * A refusal is sf-api saying no to this company: any 403 (the permission check sends
 * `{ message, required, missing }` and the company check `{ message, reason_code }`, neither
 * with a `code`), or another 4xx with its own code (for example DELETION_ALREADY_FORCED). A 4xx
 * with no code, such as an older server's 404, is a failure.
 */
export const isForceRefusal = (err: unknown) => {
  const response = (err as HttpError | undefined)?.response;
  const status = response?.status ?? 0;
  return status === 403 || (status >= 400 && status < 500 && !!text(response?.data?.code));
};

export interface ForceBatchResult {
  result: BatchResult;
  /** Ids sf-api refused (the rest of `result.failed` failed). */
  refused: Set<string>;
}

/** Posts a force for every company in `ids`, four at a time (the shared runner's default). */
export async function forceDeletionForCompanies(
  provider: DataProvider,
  ids: string[],
): Promise<ForceBatchResult> {
  const refused = new Set<string>();
  const result = await runBatch(
    ids,
    async (id) => {
      try {
        await provider.custom!({ url: forceDeletionUrl(id), method: 'post', payload: {} });
      } catch (err) {
        if (isForceRefusal(err)) refused.add(id);
        throw err;
      }
      return 'updated';
    },
    undefined,
    forceErrorMessage,
  );
  return { result, refused };
}
